import { useCallback, useMemo, useRef } from "react"

import { useScopedStorageKey } from "@/workspaces/scope"

import { logger } from "@/lib/logger"
import {
  normalizeSerializedCursors,
  runsSessionStateSchema,
} from "@/utils/validation/externalBoundarySchemas"
import { warnOnInvalidExternalData } from "@/utils/validation/externalBoundaryValidation"

import { isRunsViewMode, type RunsViewMode } from "../types/viewMode"

const log = logger("RunsSessionState")

const SESSION_KEY_BASE = "runs-session-state"
const SESSION_EXPIRY_MS = 30 * 60 * 1000 // 30 minutes

interface RunsSessionState {
  /** All page boundary cursors for reloading pages */
  loadedCursors: (string | undefined)[]
  /** Exact scroll position in pixels */
  scrollOffset: number
  /** Row that was clicked (for "you were here" highlight) */
  clickedRowId: string
  /** Timestamp for expiry check */
  timestamp: number
  /** View mode at time of save */
  viewMode: RunsViewMode
}

/**
 * Hook for saving/restoring runs table state via sessionStorage.
 * Enables back-button restoration with scroll position and row highlight.
 */
export function useRunsSessionState() {
  const highlightedRowRef = useRef<string | null>(null)

  // Scoped by org + workspace so a workspace switch can't resurrect another
  // workspace's cursors/view mode via the sessionStorage back-button
  // restoration path. Kept in a ref (not state) since the callbacks below
  // fire imperatively, not from an effect.
  const sessionKey = useScopedStorageKey(SESSION_KEY_BASE)
  const sessionKeyRef = useRef(sessionKey)
  sessionKeyRef.current = sessionKey

  const saveBeforeNavigation = useCallback(
    (params: {
      clickedRowId: string
      scrollOffset: number
      loadedCursors: (string | undefined)[]
      viewMode: RunsViewMode
    }) => {
      const key = sessionKeyRef.current
      if (!key) {
        return
      }

      const state: RunsSessionState = {
        loadedCursors: params.loadedCursors,
        scrollOffset: params.scrollOffset,
        clickedRowId: params.clickedRowId,
        timestamp: Date.now(),
        viewMode: params.viewMode,
      }

      try {
        sessionStorage.setItem(key, JSON.stringify(state))
      } catch (error) {
        log.error("Failed to save runs session state", error)
      }
    },
    [],
  )

  const getSavedState = useCallback((): RunsSessionState | null => {
    try {
      const key = sessionKeyRef.current
      const saved = key ? sessionStorage.getItem(key) : null
      if (!saved) {
        return null
      }

      const state = warnOnInvalidExternalData(
        JSON.parse(saved) as Partial<RunsSessionState>,
        runsSessionStateSchema,
        {
          boundary: "Runs session state",
          log,
        },
      )
      if (
        typeof state.timestamp !== "number" ||
        typeof state.clickedRowId !== "string" ||
        typeof state.scrollOffset !== "number" ||
        !Array.isArray(state.loadedCursors) ||
        !isRunsViewMode(state.viewMode)
      ) {
        if (key) {
          sessionStorage.removeItem(key)
        }
        return null
      }

      const isExpired = Date.now() - state.timestamp > SESSION_EXPIRY_MS
      if (isExpired) {
        if (key) {
          sessionStorage.removeItem(key)
        }
        return null
      }

      return {
        ...state,
        loadedCursors: normalizeSerializedCursors(state.loadedCursors),
      } as RunsSessionState
    } catch {
      return null
    }
  }, [])

  const restoreScrollAndHighlight = useCallback(
    (
      scrollContainer: HTMLDivElement | null,
    ): {
      clickedRowId: string | null
      scrollOffset: number
      viewMode: RunsViewMode
    } | null => {
      const state = getSavedState()
      if (!state) {
        return null
      }

      if (sessionKeyRef.current) {
        sessionStorage.removeItem(sessionKeyRef.current)
      }
      highlightedRowRef.current = state.clickedRowId

      if (scrollContainer) {
        requestAnimationFrame(() => {
          scrollContainer.scrollTo({
            top: state.scrollOffset,
            behavior: "instant",
          })
        })
      }

      return {
        clickedRowId: state.clickedRowId,
        scrollOffset: state.scrollOffset,
        viewMode: state.viewMode,
      }
    },
    [getSavedState],
  )

  const getHighlightedRowId = useCallback((): string | null => {
    return highlightedRowRef.current
  }, [])

  const clearHighlightAfterDelay = useCallback((delayMs = 3000) => {
    setTimeout(() => {
      highlightedRowRef.current = null
    }, delayMs)
  }, [])

  const clearSavedState = useCallback(() => {
    try {
      if (sessionKeyRef.current) {
        sessionStorage.removeItem(sessionKeyRef.current)
      }
      highlightedRowRef.current = null
    } catch {
      // Ignore errors
    }
  }, [])

  return useMemo(
    () => ({
      saveBeforeNavigation,
      getSavedState,
      restoreScrollAndHighlight,
      getHighlightedRowId,
      clearHighlightAfterDelay,
      clearSavedState,
    }),
    [
      saveBeforeNavigation,
      getSavedState,
      restoreScrollAndHighlight,
      getHighlightedRowId,
      clearHighlightAfterDelay,
      clearSavedState,
    ],
  )
}
